import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  useTheme
} from '@mui/material'
import {
  TrendingUp,
  People,
  Receipt,
  AttachMoney,
  Warning,
  CheckCircle,
  Schedule,
  Add,
  ShoppingCart
} from '@mui/icons-material'

import useAuthStore from '../../store/authStore'
import { formatCurrency, formatUserRole, formatDate, formatDaysUntil } from '../../utils/formatters'
import { useDashboard } from '../../hooks/useDashboard'
import KPICard from '../../components/Dashboard/KPICard'
import SalesChart from '../../components/Dashboard/SalesChart'
import StatusPieChart from '../../components/Dashboard/StatusPieChart'
import TopDebtorsChart from '../../components/Dashboard/TopDebtorsChart'
import RoleGuard from '../../components/Auth/RoleGuard'
import ApiTest from '../../components/Debug/ApiTest'

const DashboardPage = () => {
  const { user, isAdmin } = useAuthStore()
  const navigate = useNavigate()
  const [period, setPeriod] = useState('month')
  const theme = useTheme()
  
  const { data: dashboardData, isLoading, error } = useDashboard(period)

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value)
  }

  const handleCreateClient = () => {
    navigate('/clients')
  }

  const handleCreateInvoice = () => {
    navigate('/invoices')
  }

  const handleCreatePurchaseOrder = () => {
    navigate('/purchase-orders')
  }

  const handleViewReports = () => {
    navigate('/reports')
  }

  const handleViewClient = (clientId) => {
    navigate(`/clients/${clientId}`)
  }

  const handleViewInvoice = (invoiceId) => {
    navigate(`/invoices/${invoiceId}`)
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dashboard
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Error cargando dashboard: {error.message}
        </Alert>
      </Box>
    )
  }

  const indicators = dashboardData?.indicators || {}
  const charts = dashboardData?.charts || {}
  const upcomingDue = dashboardData?.upcomingDue || []

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body1" color="text.secondary">
              Bienvenido de vuelta, {user?.name}
            </Typography>
            <Chip 
              label={formatUserRole(user?.role)}
              color={user?.role === 'ADMINISTRADOR' ? 'primary' : 'default'}
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={period}
              onChange={handlePeriodChange}
              label="Período"
            >
              <MenuItem value="month">Este Mes</MenuItem>
              <MenuItem value="year">Este Año</MenuItem>
              <MenuItem value="all">Todo</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <KPICard
            title="Clientes Activos"
            value={indicators.totals?.clients}
            icon={<People />}
            color="primary"
            subtitle="Total de clientes registrados"
            loading={isLoading}
            format="number"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <KPICard
            title="Total Facturas"
            value={indicators.totals?.invoices}
            icon={<Receipt />}
            color="info"
            subtitle="Facturas en el sistema"
            loading={isLoading}
            format="number"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <KPICard
            title="Por Cobrar"
            value={indicators.pending?.totalAmount}
            icon={<AttachMoney />}
            color="warning"
            subtitle={`${indicators.pending?.invoices || 0} facturas pendientes`}
            loading={isLoading}
            format="currency"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <KPICard
            title="Vencidas"
            value={indicators.overdue?.totalAmount}
            icon={<Warning />}
            color="error"
            subtitle={`${indicators.overdue?.invoices || 0} facturas vencidas`}
            loading={isLoading}
            format="currency"
          />
        </Grid>
      </Grid>

      {/* Secondary KPIs */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <KPICard
            title="Facturado"
            value={indicators.periodData?.totalAmount}
            icon={<TrendingUp />}
            color="success"
            subtitle={`${period === 'month' ? 'Este mes' : period === 'year' ? 'Este año' : 'Total'}`}
            loading={isLoading}
            format="currency"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <KPICard
            title="Pagado"
            value={indicators.paid?.totalAmount}
            icon={<CheckCircle />}
            color="success"
            subtitle={`${indicators.paid?.invoices || 0} facturas pagadas`}
            loading={isLoading}
            format="currency"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <KPICard
            title="% Recuperación"
            value={indicators.recoveryPercentage}
            icon={<TrendingUp />}
            color="info"
            subtitle="Porcentaje de cobro"
            loading={isLoading}
            format="percentage"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <KPICard
            title="Próximas a Vencer"
            value={upcomingDue?.length}
            icon={<Schedule />}
            color="warning"
            subtitle={`Próximos ${dashboardData?.config?.upcomingDays || 7} días`}
            loading={isLoading}
            format="number"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* Sales Evolution Chart */}
        <Grid item xs={12} lg={8}>
          <SalesChart 
            data={charts.monthlyEvolution}
            loading={isLoading}
            title="Evolución de Ventas Mensuales"
          />
        </Grid>
        
        {/* Status Distribution */}
        <Grid item xs={12} lg={4}>
          <StatusPieChart 
            data={charts.statusDistribution}
            loading={isLoading}
            title="Distribución por Estado"
          />
        </Grid>
      </Grid>

      {/* Bottom Section */}
      <Grid container spacing={2}>
        {/* Top Debtors */}
        <Grid item xs={12} lg={8}>
          <TopDebtorsChart 
            data={charts.topDebtors}
            loading={isLoading}
            title="Top Clientes Deudores"
          />
        </Grid>
        
        {/* Upcoming Due & Quick Actions */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={2}>
            {/* Quick Actions */}
            <Grid item xs={12}>
              <Card sx={{ 
                background: theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                  : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                    ⚡ Acciones Rápidas
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                    <RoleGuard requireAdmin fallback={null}>
                      <Button 
                        variant="contained" 
                        startIcon={<ShoppingCart />}
                        onClick={handleCreatePurchaseOrder}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        Nueva OC
                      </Button>
                    </RoleGuard>
                    
                    <RoleGuard requireAdmin fallback={null}>
                      <Button 
                        variant="contained" 
                        startIcon={<Receipt />}
                        onClick={handleCreateInvoice}
                        fullWidth
                      >
                        Nueva Factura
                      </Button>
                    </RoleGuard>
                    
                    <RoleGuard requireAdmin fallback={null}>
                      <Button 
                        variant="outlined" 
                        startIcon={<People />}
                        onClick={handleCreateClient}
                        fullWidth
                      >
                        Nuevo Cliente
                      </Button>
                    </RoleGuard>
                    
                    <Button 
                      variant="outlined" 
                      startIcon={<TrendingUp />}
                      onClick={handleViewReports}
                      fullWidth
                    >
                      Ver Reportes
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Upcoming Due Invoices */}
            <Grid item xs={12}>
              <Card sx={{ 
                background: upcomingDue?.length > 0 ? 
                  (theme.palette.mode === 'dark' 
                    ? `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.main}08 100%)`
                    : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)') : 
                  (theme.palette.mode === 'dark' 
                    ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.main}08 100%)`
                    : 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%)'),
                border: '1px solid',
                borderColor: upcomingDue?.length > 0 ? 'warning.main' : 'success.main',
                borderOpacity: 0.3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                    🕐 Próximas a Vencer
                  </Typography>
                  
                  {upcomingDue && upcomingDue.length > 0 ? (
                    <List dense>
                      {upcomingDue.slice(0, 5).map((invoice, index) => (
                        <React.Fragment key={invoice.id}>
                          <ListItem 
                            sx={{ px: 0, cursor: 'pointer' }}
                            onClick={() => handleViewInvoice(invoice.id)}
                          >
                            <ListItemAvatar>
                              <Avatar 
                                sx={{ 
                                  bgcolor: invoice.daysUntilDue <= 2 ? 'error.main' : 'warning.main',
                                  width: 32,
                                  height: 32,
                                  fontSize: '0.75rem'
                                }}
                              >
                                {invoice.daysUntilDue}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={invoice.invoiceNumber}
                              secondary={`${invoice.client?.name} • ${formatCurrency(invoice.totalAmount)}`}
                              primaryTypographyProps={{
                                variant: "body2",
                                fontWeight: 600
                              }}
                              secondaryTypographyProps={{
                                variant: "caption",
                                color: "text.secondary"
                              }}
                            />
                          </ListItem>
                          {index < Math.min(4, upcomingDue.length - 1) && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No hay facturas próximas a vencer en {dashboardData?.config?.upcomingDays || 7} días
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Debug Section - Remove this in production */}
      {process.env.NODE_ENV === 'development' && (
        <ApiTest />
      )}
    </Box>
  )
}

export default DashboardPage
